'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Settings,
  Users,
  Bell,
  Shield,
  Building2,
  Clock,
  CreditCard,
  ChevronRight,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  UserCog,
  ToggleLeft,
  ToggleRight,
  Search,
  MoreVertical,
  UserPlus,
  Pencil,
  Ban,
  RefreshCw,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type UserRole = 'admin' | 'manager' | 'staff';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  department: string;
  phone: string;
  is_active: boolean;
  notification_email: boolean;
  notification_sms: boolean;
  notification_push: boolean;
  theme: string;
  created_at: string;
}

interface SystemSetting {
  key: string;
  value: string;
  label: string;
  category: string;
}

type SettingsTab = 'profile' | 'notifications' | 'users' | 'organization' | 'scheduling' | 'payroll' | 'compliance';

// ─── Role badge ───────────────────────────────────────────────────────────────

const RoleBadge = ({ role }: { role: UserRole }) => {
  const map: Record<UserRole, string> = {
    admin: 'bg-purple-100 text-purple-700',
    manager: 'bg-blue-100 text-blue-700',
    staff: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${map[role]}`}>
      {role}
    </span>
  );
};

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type }: { message: string; type: 'success' | 'error' }) => (
  <div
    className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
      type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}
  >
    {type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
    {message}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { user } = useAuth();
  const supabase = createClient();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole>('staff');
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Admin-only state
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // System settings state
  const [sysSettings, setSysSettings] = useState<Record<string, string>>({});
  const [sysSettingsLoading, setSysSettingsLoading] = useState(false);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load current user profile ─────────────────────────────────────────────

  const loadProfile = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      setProfile(data as UserProfile);
      setUserRole(data.role as UserRole);
    } catch {
      showToast('Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // ── Load all users (admin only) ───────────────────────────────────────────

  const loadAllUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('full_name');
      if (error) throw error;
      setAllUsers((data as UserProfile[]) || []);
    } catch {
      showToast('Failed to load users', 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [supabase]);

  // ── Load system settings (admin only) ────────────────────────────────────

  const loadSysSettings = useCallback(async () => {
    setSysSettingsLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value, label, category');
      if (error) throw error;
      const map: Record<string, string> = {};
      (data as SystemSetting[]).forEach((s) => { map[s.key] = s.value; });
      setSysSettings(map);
    } catch {
      showToast('Failed to load system settings', 'error');
    } finally {
      setSysSettingsLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (userRole === 'admin') {
      if (activeTab === 'users') loadAllUsers();
      if (['organization', 'scheduling', 'payroll', 'compliance'].includes(activeTab)) loadSysSettings();
    }
  }, [activeTab, userRole, loadAllUsers, loadSysSettings]);

  // ── Save own profile ──────────────────────────────────────────────────────

  const saveProfile = async () => {
    if (!profile || !user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: profile.full_name,
          phone: profile.phone,
          department: profile.department,
          theme: profile.theme,
        })
        .eq('id', user.id);
      if (error) throw error;
      showToast('Profile saved successfully', 'success');
    } catch {
      showToast('Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Save notification preferences ─────────────────────────────────────────

  const saveNotifications = async () => {
    if (!profile || !user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          notification_email: profile.notification_email,
          notification_sms: profile.notification_sms,
          notification_push: profile.notification_push,
        })
        .eq('id', user.id);
      if (error) throw error;
      showToast('Notification preferences saved', 'success');
    } catch {
      showToast('Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Update user role / status (admin) ─────────────────────────────────────

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role: newRole })
        .eq('id', userId);
      if (error) throw error;
      setAllUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: newRole } : u));
      showToast('Role updated', 'success');
    } catch {
      showToast('Failed to update role', 'error');
    }
    setOpenMenuId(null);
  };

  const toggleUserActive = async (userId: string, currentActive: boolean) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_active: !currentActive })
        .eq('id', userId);
      if (error) throw error;
      setAllUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_active: !currentActive } : u));
      showToast(`User ${!currentActive ? 'activated' : 'deactivated'}`, 'success');
    } catch {
      showToast('Failed to update user status', 'error');
    }
    setOpenMenuId(null);
  };

  // ── Save system setting ───────────────────────────────────────────────────

  const saveSysSetting = async (key: string, value: string) => {
    try {
      const { error } = await supabase
        .from('system_settings')
        .update({ value, updated_by: user?.id })
        .eq('key', key);
      if (error) throw error;
      showToast('Setting saved', 'success');
    } catch {
      showToast('Failed to save setting', 'error');
    }
  };

  // ── Tab config ────────────────────────────────────────────────────────────

  const allTabs: { id: SettingsTab; label: string; icon: React.ElementType; roles: UserRole[] }[] = [
    { id: 'profile',      label: 'My Profile',      icon: UserCog,    roles: ['admin', 'manager', 'staff'] },
    { id: 'notifications',label: 'Notifications',   icon: Bell,       roles: ['admin', 'manager', 'staff'] },
    { id: 'users',        label: 'User Management', icon: Users,      roles: ['admin'] },
    { id: 'organization', label: 'Organization',    icon: Building2,  roles: ['admin'] },
    { id: 'scheduling',   label: 'Scheduling',      icon: Clock,      roles: ['admin', 'manager'] },
    { id: 'payroll',      label: 'Payroll',         icon: CreditCard, roles: ['admin', 'manager'] },
    { id: 'compliance',   label: 'Compliance',      icon: Shield,     roles: ['admin', 'manager'] },
  ];

  const visibleTabs = allTabs.filter((t) => t.roles.includes(userRole));

  const filteredUsers = allUsers.filter(
    (u) =>
      u.full_name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.department?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Settings size={22} className="text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-700 text-foreground">Settings</h1>
            <p className="text-sm text-muted-foreground">
              {userRole === 'admin' ? 'Manage your account and system configuration' : 'Manage your account preferences'}
            </p>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar nav */}
          <aside className="w-52 flex-shrink-0">
            <nav className="space-y-0.5">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    <span className="flex-1">{tab.label}</span>
                    {active && <ChevronRight size={14} />}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">

            {/* ── MY PROFILE ── */}
            {activeTab === 'profile' && profile && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-5">
                <h2 className="text-base font-600 text-foreground">Profile Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Full Name</label>
                    <input
                      type="text"
                      value={profile.full_name}
                      onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Email</label>
                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-lg text-muted-foreground cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Phone</label>
                    <input
                      type="tel"
                      value={profile.phone || ''}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Department</label>
                    <input
                      type="text"
                      value={profile.department || ''}
                      onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Role</label>
                    <div className="flex items-center h-9">
                      <RoleBadge role={profile.role as UserRole} />
                      <span className="ml-2 text-xs text-muted-foreground">(assigned by admin)</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-muted-foreground mb-1.5">Theme</label>
                    <select
                      value={profile.theme}
                      onChange={(e) => setProfile({ ...profile, theme: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    >
                      <option value="system">System Default</option>
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                </div>
                <div className="pt-2 flex justify-end">
                  <button
                    onClick={saveProfile}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save Changes
                  </button>
                </div>
              </div>
            )}

            {/* ── NOTIFICATIONS ── */}
            {activeTab === 'notifications' && profile && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-5">
                <h2 className="text-base font-600 text-foreground">Notification Preferences</h2>
                <div className="space-y-4">
                  {[
                    { key: 'notification_email' as const, label: 'Email Notifications', desc: 'Receive updates and alerts via email' },
                    { key: 'notification_sms' as const, label: 'SMS Notifications', desc: 'Receive urgent alerts via text message' },
                    { key: 'notification_push' as const, label: 'Push Notifications', desc: 'Receive in-app push notifications' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between p-4 bg-muted/40 rounded-lg">
                      <div>
                        <p className="text-sm font-500 text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                      </div>
                      <button
                        onClick={() => setProfile({ ...profile, [key]: !profile[key] })}
                        className="text-primary hover:text-primary/80 transition-colors"
                      >
                        {profile[key]
                          ? <ToggleRight size={28} className="text-primary" />
                          : <ToggleLeft size={28} className="text-muted-foreground" />}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="pt-2 flex justify-end">
                  <button
                    onClick={saveNotifications}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save Preferences
                  </button>
                </div>
              </div>
            )}

            {/* ── USER MANAGEMENT (Admin only) ── */}
            {activeTab === 'users' && userRole === 'admin' && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-600 text-foreground">User Management</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={loadAllUsers}
                      className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
                      title="Refresh"
                    >
                      <RefreshCw size={15} />
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors">
                      <UserPlus size={13} />
                      Invite User
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                  />
                </div>

                {usersLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 size={24} className="animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Name</th>
                          <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Email</th>
                          <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Role</th>
                          <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Department</th>
                          <th className="text-left text-xs font-600 text-muted-foreground pb-2 pr-4">Status</th>
                          <th className="text-left text-xs font-600 text-muted-foreground pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                            <td className="py-3 pr-4 font-500 text-foreground">{u.full_name || '—'}</td>
                            <td className="py-3 pr-4 text-muted-foreground">{u.email}</td>
                            <td className="py-3 pr-4">
                              {editingUser?.id === u.id ? (
                                <select
                                  value={editingUser.role}
                                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as UserRole })}
                                  onBlur={async () => {
                                    await updateUserRole(editingUser.id, editingUser.role);
                                    setEditingUser(null);
                                  }}
                                  autoFocus
                                  className="text-xs px-2 py-1 bg-background border border-border rounded-md focus:outline-none"
                                >
                                  <option value="admin">Admin</option>
                                  <option value="manager">Manager</option>
                                  <option value="staff">Staff</option>
                                </select>
                              ) : (
                                <RoleBadge role={u.role} />
                              )}
                            </td>
                            <td className="py-3 pr-4 text-muted-foreground">{u.department || '—'}</td>
                            <td className="py-3 pr-4">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-3 relative">
                              <button
                                onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                                className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors"
                              >
                                <MoreVertical size={15} />
                              </button>
                              {openMenuId === u.id && (
                                <div className="absolute right-0 top-8 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
                                  <button
                                    onClick={() => { setEditingUser(u); setOpenMenuId(null); }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors"
                                  >
                                    <Pencil size={12} /> Change Role
                                  </button>
                                  <button
                                    onClick={() => toggleUserActive(u.id, u.is_active)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted transition-colors"
                                  >
                                    <Ban size={12} /> {u.is_active ? 'Deactivate' : 'Activate'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                        {filteredUsers.length === 0 && (
                          <tr>
                            <td colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                              No users found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── ORGANIZATION (Admin only) ── */}
            {activeTab === 'organization' && userRole === 'admin' && (
              <SystemSettingsPanel
                title="Organization Settings"
                keys={['org_name', 'org_timezone', 'org_date_format']}
                sysSettings={sysSettings}
                setSysSettings={setSysSettings}
                onSave={saveSysSetting}
                loading={sysSettingsLoading}
              />
            )}

            {/* ── SCHEDULING (Admin + Manager) ── */}
            {activeTab === 'scheduling' && ['admin', 'manager'].includes(userRole) && (
              <SystemSettingsPanel
                title="Scheduling Settings"
                keys={['shift_min_hours', 'shift_max_hours', 'ot_threshold']}
                sysSettings={sysSettings}
                setSysSettings={setSysSettings}
                onSave={saveSysSetting}
                loading={sysSettingsLoading}
                readOnly={userRole === 'manager'}
              />
            )}

            {/* ── PAYROLL (Admin + Manager) ── */}
            {activeTab === 'payroll' && ['admin', 'manager'].includes(userRole) && (
              <SystemSettingsPanel
                title="Payroll Settings"
                keys={['auto_approve_timesheets', 'pay_period', 'invoice_prefix']}
                sysSettings={sysSettings}
                setSysSettings={setSysSettings}
                onSave={saveSysSetting}
                loading={sysSettingsLoading}
                readOnly={userRole === 'manager'}
              />
            )}

            {/* ── COMPLIANCE (Admin + Manager) ── */}
            {activeTab === 'compliance' && ['admin', 'manager'].includes(userRole) && (
              <SystemSettingsPanel
                title="Compliance Settings"
                keys={['credential_warn_days']}
                sysSettings={sysSettings}
                setSysSettings={setSysSettings}
                onSave={saveSysSetting}
                loading={sysSettingsLoading}
                readOnly={userRole === 'manager'}
              />
            )}
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} />}

      {/* Close dropdown on outside click */}
      {openMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
      )}
    </AppLayout>
  );
}

// ─── System Settings Panel ────────────────────────────────────────────────────

interface SystemSettingsPanelProps {
  title: string;
  keys: string[];
  sysSettings: Record<string, string>;
  setSysSettings: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onSave: (key: string, value: string) => Promise<void>;
  loading: boolean;
  readOnly?: boolean;
}

const SETTING_LABELS: Record<string, string> = {
  org_name: 'Organization Name',
  org_timezone: 'Default Timezone',
  org_date_format: 'Date Format',
  shift_min_hours: 'Minimum Shift Hours',
  shift_max_hours: 'Maximum Shift Hours',
  ot_threshold: 'Overtime Threshold (hrs/week)',
  credential_warn_days: 'Credential Warning Days',
  auto_approve_timesheets: 'Auto-Approve Timesheets',
  pay_period: 'Pay Period',
  invoice_prefix: 'Invoice Number Prefix',
};

const SETTING_DESCS: Record<string, string> = {
  org_name: 'The name displayed across the platform',
  org_timezone: 'Default timezone for scheduling and reporting',
  org_date_format: 'How dates are displayed throughout the app',
  shift_min_hours: 'Minimum hours allowed for a single shift',
  shift_max_hours: 'Maximum hours allowed for a single shift',
  ot_threshold: 'Weekly hours before overtime rate applies',
  credential_warn_days: 'Days before expiry to trigger renewal alerts',
  auto_approve_timesheets: 'Automatically approve timesheets without manager review',
  pay_period: 'How often payroll is processed',
  invoice_prefix: 'Prefix added to all invoice numbers',
};

function SystemSettingsPanel({
  title, keys, sysSettings, setSysSettings, onSave, loading, readOnly = false,
}: SystemSettingsPanelProps) {
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const handleSave = async (key: string) => {
    setSavingKey(key);
    await onSave(key, sysSettings[key] ?? '');
    setSavingKey(null);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 flex items-center justify-center h-40">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-600 text-foreground">{title}</h2>
        {readOnly && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">View only</span>
        )}
      </div>
      <div className="space-y-4">
        {keys.map((key) => (
          <div key={key} className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-500 text-foreground">{SETTING_LABELS[key] ?? key}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{SETTING_DESCS[key] ?? ''}</p>
              {!readOnly && (
                <div className="mt-2">
                  {key === 'auto_approve_timesheets' ? (
                    <button
                      onClick={() =>
                        setSysSettings((prev) => ({
                          ...prev,
                          [key]: prev[key] === 'true' ? 'false' : 'true',
                        }))
                      }
                      className="text-primary"
                    >
                      {sysSettings[key] === 'true'
                        ? <ToggleRight size={28} className="text-primary" />
                        : <ToggleLeft size={28} className="text-muted-foreground" />}
                    </button>
                  ) : key === 'pay_period' ? (
                    <select
                      value={sysSettings[key] ?? ''}
                      onChange={(e) => setSysSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="px-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="semimonthly">Semi-monthly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  ) : key === 'org_timezone' ? (
                    <select
                      value={sysSettings[key] ?? ''}
                      onChange={(e) => setSysSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="px-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    >
                      <option value="America/New_York">Eastern (ET)</option>
                      <option value="America/Chicago">Central (CT)</option>
                      <option value="America/Denver">Mountain (MT)</option>
                      <option value="America/Los_Angeles">Pacific (PT)</option>
                    </select>
                  ) : key === 'org_date_format' ? (
                    <select
                      value={sysSettings[key] ?? ''}
                      onChange={(e) => setSysSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="px-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={sysSettings[key] ?? ''}
                      onChange={(e) => setSysSettings((prev) => ({ ...prev, [key]: e.target.value }))}
                      className="px-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground w-48"
                    />
                  )}
                </div>
              )}
              {readOnly && (
                <p className="mt-1 text-sm font-500 text-foreground">{sysSettings[key] ?? '—'}</p>
              )}
            </div>
            {!readOnly && (
              <button
                onClick={() => handleSave(key)}
                disabled={savingKey === key}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors mt-6"
              >
                {savingKey === key ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
